import type { ButtonTypes } from "@/components/button";

export const ACCOUNT_ROLE_VALUES = [
  "super_admin",
  "admin",
  "3d_designer",
  "moderator",
  "user",
  "licensed",
  "demo",
] as const;

export const ACCOUNT_ROLE_TAB_ITEMS = [
  { value: "all", label: "All roles" },
  { value: "user", label: "User" },
  { value: "moderator", label: "Moderator" },
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super admin" },
  { value: "3d_designer", label: "3D designer" },
  { value: "demo", label: "Demo" },
  { value: "licensed", label: "Licensed" },
] as const;

export const ACCOUNT_SUBSCRIPTION_OPTIONS = [
  { value: "all", label: "All subscriptions" },
  { value: "with-subscription", label: "With subscription" },
  { value: "without-subscription", label: "Without subscription" },
] as const;

export const ACCOUNT_PERIOD_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "last-24-hours", label: "Last 24 hours" },
  { value: "last-7-days", label: "Last 7 days" },
  { value: "last-28-days", label: "Last 28 days" },
] as const;

export type AccountRole = (typeof ACCOUNT_ROLE_VALUES)[number];
export type AccountRoleFilter = (typeof ACCOUNT_ROLE_TAB_ITEMS)[number]["value"];
export type AccountSubscriptionFilter =
  (typeof ACCOUNT_SUBSCRIPTION_OPTIONS)[number]["value"];
export type AccountPeriodFilter = (typeof ACCOUNT_PERIOD_OPTIONS)[number]["value"];

type BadgeVariant = NonNullable<ButtonTypes["variant"]>;

const ACCOUNT_ROLE_META: Record<AccountRole, { label: string; variant: BadgeVariant }> = {
  super_admin: {
    label: "Super admin",
    variant: "primary",
  },
  admin: {
    label: "Admin",
    variant: "danger",
  },
  "3d_designer": {
    label: "3D designer",
    variant: "primary",
  },
  moderator: {
    label: "Moderator",
    variant: "warning",
  },
  user: {
    label: "User",
    variant: "secondary",
  },
  licensed: {
    label: "Licensed",
    variant: "success",
  },
  demo: {
    label: "Demo",
    variant: "warning",
  },
};

export const getAccountRoleMeta = (role: AccountRole) => ACCOUNT_ROLE_META[role];

export const CREATE_ACCOUNT_ROLE_OPTIONS: Array<{ value: AccountRole; label: string }> =
  ACCOUNT_ROLE_VALUES.map((value) => ({
    value,
    label: ACCOUNT_ROLE_META[value].label,
  }));

export const getAccountSubscriptionMeta = (hasSubscription: boolean) => ({
  label: hasSubscription ? "Subscribed" : "No subscription",
  variant: (hasSubscription ? "success" : "secondary") as BadgeVariant,
});

export const formatAccountTimestamp = (timestamp: number | null) => {
  if (!timestamp) return "Never";

  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
};
